import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { getAgencyDetail, postAgency, putAgency } from "store/agency/thunk";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  AGENCY_BRANCH_STATUS_OPTION,
  COLOR_AGENCY_BRANCH_STATUS,
} from "helpers/constans";
import CustomParametersForm from "components/Common/CustomParametersForm";
import { IParams } from "api/types/_params";
import { formatDateStringToKorea } from "helpers/format";
import { unique } from "helpers/validate_yup";
import UploadFileBK from "components/Common/UploadFileBK";
import DropdownChannel from "components/Common/DropdownChannel";
import 'react-phone-number-input/style.css'
import PhoneInput from "components/Common/PhoneInput";

export interface Props {
  id?: string;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  setLinkImage?: any;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

Yup.addMethod(Yup.array, "unique", unique);


const PARAMS_KEY_DEFINE = ['agency', 'campaign'];

const AgencyForm = ({
  id = "",
  triggerRefresh,
  isShowDetail = false,
  setLinkImage,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const filterDataParams = values?.params?.filter(
        (item: IParams) => item?.paramKey || item?.paramValue
      );
      const formatDataParams =
        filterDataParams?.length > 0
          ? filterDataParams?.map((item: IParams, index: number) => ({
              ordering: item?.ordering,
              paramKey: item?.paramKey,
              paramValue: item?.paramValue,
              description: item?.description,
              status: item?.status,
            }))
          : [];

      const data = {
        channel: values?.channel,
        agencyName: values?.agencyName,
        avatar: values?.avatar?.length > 0 ? values?.avatar?.[0]?.url : '',
        contactName: values?.contactName,
        email: values?.email,
        phone: values?.phone,
        status: values?.status,
        ...(formatDataParams?.length > 0 ? { params: formatDataParams} : null)
      };

      const response: any = id
        ? await putAgency(id, data)
        : await postAgency(data);

      if (response?.data || response?.code == 200) {
        setIsLoading((_prev) => false);
        toast( 
          `${id ? t("The agency has been updated.") : t("The agency has been created.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    agencyName: Yup.string().required(`${t("Agency Name is required")}`),
    channel: Yup.string().required(`${t("Channel is required")}`),
    params: Yup.array()
      .of(
        Yup.object().shape({
          paramKey: Yup.string().matches(
            /^[A-Za-z]+$/,
            `${t("Only allow characters, no spaces")}`
          ).test("paramKey", `${t("The key cannot be 'agency' or 'campaign'")}`, (value) => !PARAMS_KEY_DEFINE.includes(value || '')),
          paramValue: Yup.string().when("paramKey", {
            is: (value: any) => !!value,
            then: (rule) => {
              return rule.test(
                "paramKey",
                `${t("This field is required")}`,
                (target) => !!target?.trim()
              );
            },
          }),
        })
      )
      .unique("Key is duplicated", (a: any) => a.param_key),
    email: Yup.string().matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Email Address is invalid').nullable(),
  });

  const formik = useFormik({
    initialValues: {
      avatar: "",
      agencyName: "",
      contactName: "",
      channel: "store",
      phone: "",
      referralCode: "",
      branchLinkStatus: "",
      email: "",
      status: "ACTIVE",
      params: [
      ] as IParams[],
      updatedAt: "",
      createdAt: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    // console.log(valueDefault)
    let avatar = valueDefault?.avatar;
    if(valueDefault?.avatar) {
      avatar = [
        {
          thumbnailUrl: avatar,
          url: avatar
        }
      ];
    }

    formik.setValues({
      ...valueDefault,
      updatedAt: valueDefault.updatedAt
        ? formatDateStringToKorea(valueDefault.updatedAt)
        : null,
      createdAt: valueDefault.createdAt
        ? formatDateStringToKorea(valueDefault.createdAt)
        : null,
      params: valueDefault?.agencyParams ?? [],
      avatar: avatar
    });
  };

  const handleCallAgencyDetail = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      setIsLoadingDetail((_prev) => true);
      const resDetail: any = await getAgencyDetail(idItem);

      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  useEffect(() => {
    handleCallAgencyDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create Agency")} - ${t("Agency")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Agency") : t("Create Agency")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 2) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              {/* {isLoadingDetail && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 3,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: "rgb(164 164 164 / 36%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Spinner size="sm" color="primary"></Spinner>
                </div>
              )} */}
              <form onSubmit={formik.handleSubmit}>
                <Row
                  className="modal-custom-scroll"
                  style={{ overflowY: "auto", maxHeight: "68vh" }}
                >
                  {id && (
                    <Col lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="referralCode" className="form-label">
                          {t("Referral Code")}
                        </Label>
                        <Input
                          name="referralCode"
                          type="text"
                          className="form-control"
                          id="referralCode"
                          disabled={true}
                          placeholder={t("Enter your referral code")}
                          value={formik?.values?.referralCode}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "referralCode",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.referralCode &&
                        formik.errors.referralCode ? (
                          <div className="text-danger mt-2">
                            {formik.errors.referralCode}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  )}

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="agencyName" className="form-label">
                        {t("Agency Name")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="agencyName"
                        name="agencyName"
                        type="text"
                        className="form-control"
                        placeholder={t("Agency Name")}
                        value={formik?.values?.agencyName}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "agencyName",
                            event?.target?.value
                          )
                        }
                      />
                      {formik.touched.agencyName &&
                      formik.errors.agencyName ? (
                        <div className="text-danger mt-2">
                          {formik.errors.agencyName}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Channel")}{" "}
                        <span className="text-danger"> *</span>
                      </Label>

                      <DropdownChannel
                        name="channel"
                        placeholder={`${t("Channel")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.channel || null}
                        onChangeSelect={(channel: any) => {
                          formik.setFieldValue("channel", channel?.value);
                        }}
                        isHasOptionAll={false}
                      />

                      {formik?.touched?.channel && formik?.errors?.channel ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.channel}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="contactName" className="form-label">
                        {t("Contact Name")}{" "}
                      </Label>
                      <Input
                        name="contactName"
                        type="text"
                        className="form-control"
                        id="contactName"
                        placeholder={t("Contact Name")}
                        value={formik?.values?.contactName}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "contactName",
                            event?.target?.value
                          )
                        }
                      />
                      {formik.touched.contactName &&
                      formik.errors.contactName ? (
                        <div className="text-danger mt-2">
                          {formik.errors.contactName}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="phone" className="form-label">
                        {t("Contact Phone")}
                      </Label>

                      <PhoneInput
                        formikField={"phone"}
                        formik={formik}
                        className=""
                        placeholder={t("Contact Phone")}
                      />

                      {formik.touched.phone && formik.errors.phone ? (
                        <div className="text-danger mt-2">
                          {formik.errors.phone}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="emailInput" className="form-label">
                        {t("Email Address")}
                      </Label>
                      <Input
                        name="email"
                        type="text"
                        className="form-control"
                        id="emailInput"
                        placeholder={t("Email Address")}
                        value={formik?.values?.email}
                        onChange={(event: any) =>
                          formik.setFieldValue("email", event?.target?.value)
                        }
                      />
                      {formik.touched.email && formik.errors.email ? (
                        <div className="text-danger mt-2">
                          {formik.errors.email}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  {id && (
                    <>
                      <Col xxl={6}>
                        <label className="form-label">
                          {t("Linked Branch IO")}
                        </label>

                        <DropdownStatusNew
                          name="status_branch"
                          dataList={AGENCY_BRANCH_STATUS_OPTION || []}
                          placeholder={`${t("Linked Branch IO")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          initialValue={formik.values.branchLinkStatus}
                          disabled={true}
                          onChangeSelect={(event: any) => {
                            formik.setFieldValue(
                              "branchLinkStatus",
                              event?.value
                            );
                          }}
                          isHasOptionAll={false}
                          colors={COLOR_AGENCY_BRANCH_STATUS}
                        />

                        {formik.touched.branchLinkStatus &&
                        formik.errors.branchLinkStatus ? (
                          <div className="text-danger mt-2">
                            {formik.errors.branchLinkStatus}
                          </div>
                        ) : null}
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="createdAt" className="form-label">
                            {t("Created at")}
                          </Label>
                          <Input
                            name="createdAt"
                            type="text"
                            className="form-control"
                            id="createdAt"
                            disabled={true}
                            value={formik?.values?.createdAt}
                            onChange={(event: any) => {
                              console.log(event);
                            }}
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="updatedAt" className="form-label">
                            {t("Updated at")}
                          </Label>
                          <Input
                            name="email"
                            type="text"
                            className="form-control"
                            id="updatedAt"
                            disabled={true}
                            value={formik?.values?.updatedAt}
                            onChange={(event: any) => {
                              console.log(event);
                            }}
                          />
                        </div>
                      </Col>
                    </>
                  )}

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Status")}</Label>

                      <div>
                        <div
                          className="form-check form-switch form-switch-sm text-center"
                          dir="ltr"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="status"
                            checked={formik?.values?.status === "ACTIVE" ? true : false}
                            onChange={(event) => {
                              formik.setFieldValue(
                                "status",
                                event?.target?.checked
                              );
                            }}
                          />
                        </div>
                      </div>

                      {formik?.touched?.status && formik?.errors?.status ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.status}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xxl={12}>
                    <div className="mb-3">
                      <label className="form-label">{t("Avatar")}</label>
                      <UploadFileBK
                        formik={formik}
                        fieldValue="avatar"
                        isMultiple={false}
                        isUploadMore={
                          !formik?.values?.avatar ||
                          !(formik?.values?.avatar as any)?.length
                        }
                        setLinkImage={setLinkImage}
                      />
                    </div>
                  </Col>

                  <CustomParametersForm formik={formik} />
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Button Update Agency")
                          : t("Button Create Agency")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default AgencyForm;
